import React from 'react';
import { Edit, Plus } from 'lucide-react';
import RightPanel from '../../components/RightPanel';
import { useContactsList } from './hooks/useContactsList';
import { useContactPanel } from './hooks/useContactPanel';
import ContactFilters from './components/ContactFilters';
import ContactsTable from './components/ContactsTable';
import ContactViewPanel from './components/ContactViewPanel';
import ContactEditForm from './components/ContactEditForm';

const CustomerContactsPage: React.FC = () => {
  const {
    customers,
    selectedCustomerFilter,
    setSelectedCustomerFilter,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filteredContacts,
    load,
  } = useContactsList();

  const {
    selectedContact,
    viewMode,
    submitting,
    loadingContact,
    panelError,
    form,
    handleViewDetails,
    handleEdit,
    handleCreate,
    handleClosePanel,
    handleFormChange,
    handleSubmit,
    handleDelete,
  } = useContactPanel(load, selectedCustomerFilter);

  return (
    <div className="theme-card rounded-2xl p-3 md:p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold theme-text-primary">Customer Contacts</h2>
        <button
          onClick={handleCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Contact
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm">
          {error}
        </div>
      )}

      <ContactFilters
        customers={customers}
        selectedCustomerFilter={selectedCustomerFilter}
        searchQuery={searchQuery}
        onCustomerChange={setSelectedCustomerFilter}
        onRefresh={() => load()}
        onSearchChange={setSearchQuery}
      />

      <ContactsTable
        filteredContacts={filteredContacts}
        loading={loading}
        searchQuery={searchQuery}
        loadingContact={loadingContact}
        onView={handleViewDetails}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Right Side Panel for View Details / Edit */}
      <RightPanel
        isOpen={!!viewMode}
        onClose={handleClosePanel}
        title={viewMode === 'view' ? 'Contact Details' : selectedContact ? 'Edit Contact' : 'Add Contact'}
        widthClass="w-full md:w-1/2"
        headerExtras={
          viewMode === 'view' && selectedContact ? (
            <button
              onClick={() => handleEdit(selectedContact as any)}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
              title="Edit contact"
            >
              <Edit className="w-3.5 h-3.5" />
              Edit
            </button>
          ) : undefined
        }
      >
        {selectedContact && viewMode === 'view' && (
          <ContactViewPanel
            selectedContact={selectedContact}
            customers={customers}
            panelError={panelError}
          />
        )}

        {viewMode === 'edit' && (
          <ContactEditForm
            form={form}
            customers={customers}
            submitting={submitting}
            panelError={panelError}
            selectedContact={selectedContact}
            onChange={handleFormChange}
            onSubmit={handleSubmit}
            onCancel={handleClosePanel}
          />
        )}
      </RightPanel>
    </div>
  );
};

export default CustomerContactsPage;
